import React, { useState, useContext, useEffect, useMemo } from "react";
import {
  format,
  subMonths,
  addMonths,
  startOfWeek,
  startOfMonth,
  addDays,
  getDaysInMonth
} from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight, faEye, faClipboardList, faCloudShowersHeavy, faCalendarCheck } from "@fortawesome/free-solid-svg-icons";
import DashboardContext from "../../../../contexts/DashboardContext";
import { Link } from "react-router-dom";
import { AppDivisionContext } from "../../../../contexts/AppDivisionContext";
import { Popover, popoverClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import CalendarDayModal from "./CalendarDayModal";
import PrecipitationDayModal from "./PrecipitationDayModal";
import UpcomingDayModal from "./UpcomingDayModal";



const MonthViewLayout = (props) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const { displayStatsForCal } = useContext(DashboardContext);

  /**
   * SW2-2343 — bucket the month's inspections by day once, not once per cell.
   *
   * Every cell used to run `displayStatsForCal.filter(...)` twice over the whole month,
   * the first pass compiling a fresh `RegExp` each time. Across a division that is 31
   * cells x several thousand rows x two passes on every render, and it was about to gain
   * a third marker. One pass here, an O(1) lookup there.
   *
   * `inspectionCertDate` arrives as `M-D-YYYY`, so it is padded to `yyyy-MM-dd` — the
   * same key precipitation and upcoming already use, which is what lets all three
   * markers read their day the same way.
   */
  /**
   * SW2-2343 — today, as the same `yyyy-MM-dd` key the cells are filed under.
   *
   * Computed once here rather than per cell: 35 cells each calling `new Date()` can
   * straddle midnight and disagree with one another about which day is "today", which
   * would show one cell past due and its neighbour not.
   *
   * Held in state and refreshed at the day boundary, rather than read during render.
   * This is a wall-mounted dashboard as much as a page someone opens, so it can sit
   * untouched across midnight — and a render-time value only changes when something
   * else happens to re-render, so yesterday's inspections stayed orange instead of
   * turning red until an unrelated state change happened along. (Raised by Copilot on
   * PR #87.)
   */
  const [todayKey, setTodayKey] = useState(() => format(new Date(), 'yyyy-MM-dd'))

  useEffect(() => {
    const now = new Date()
    const nextDay = new Date(now)

    // Local midnight, matching how `todayKey` and the cells' own keys are formatted.
    nextDay.setHours(24, 0, 0, 0)

    // A second past the boundary, so a timer that fires a hair early does not format
    // the previous day again and wait another 24 hours to correct itself.
    const timer = setTimeout(
      () => setTodayKey(format(new Date(), 'yyyy-MM-dd')),
      nextDay.getTime() - now.getTime() + 1000,
    )

    // Depends on `todayKey` so each rollover schedules the next. A machine asleep at
    // midnight fires late rather than not at all, which is the right failure: the
    // markers correct themselves on wake.
    return () => clearTimeout(timer)
  }, [todayKey])

  const inspectionsByDay = useMemo(() => {
    const byDay = {};

    (displayStatsForCal ?? []).forEach((row) => {
      if (!row?.inspectionCertDate) return;

      const [m, d, y] = String(row.inspectionCertDate).split('-');

      if (!m || !d || !y) return;

      const key = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;

      byDay[key] = byDay[key] ?? [];
      byDay[key].push(row);
    });

    return byDay;
  }, [displayStatsForCal]);

  useEffect(() => {
    props.setCurrentCalMonth(format(currentMonth, 'yyyy-MM'));
  }, [currentMonth]);

  // SW2-2325 — paging only moves the month. Fetching moved up to Calendar, which owns
  // both halves of the query (the month and the selection) and so can react to either
  // changing. Previously this component fetched on click as well, which meant a month
  // change with a selection active issued the request twice.
  const changeMonthHandle = (btnType) => {
    if (btnType === "prev") setCurrentMonth(subMonths(currentMonth, 1));
    if (btnType === "next") setCurrentMonth(addMonths(currentMonth, 1));
  };

  const renderHeader = () => {
    const dateFormat = "MMMM yyyy";
    
    return (
      <div className="header row flex-middle">

        <div className="col col-end flex-gap renderHeader__left">
          <div className="icon" onClick={() => changeMonthHandle("prev")}>
            <FontAwesomeIcon icon={ faAngleLeft }/>
          </div>
        </div>

        <div className="col col-center renderHeader__center">
          <span>{format(currentMonth, dateFormat)}</span>
        </div>


        <div className="col col-start flex-gap renderHeader__right">
          <div className="icon" onClick={() => changeMonthHandle("next")}>
            <FontAwesomeIcon icon={ faAngleRight }/>
          </div>
        </div>

      </div>
    );
  };

  const renderDays = () => {
    const dateFormat = "EEE";
    const days = [];

    let startDate = startOfWeek(currentMonth, { weekStartsOn: 1 });

    for (let i = 0; i < 7; i++) {
      days.push(
        <div className="col col-center" key={i}>
          {format(addDays(startDate, i), dateFormat)}
        </div>
      );
    }
    
    return <div className="days row">{days}</div>;
  };


  const renderCells = () => {
    let firstDayOfCurrentMonth = format(startOfMonth(currentMonth), 'i') //7

    let numberOfDaysInCurrentMonth = getDaysInMonth(currentMonth) //31

    let blankCells = []

    let datesOfCurrentMonth = []

    for(let i=1; i<firstDayOfCurrentMonth; i++){
        blankCells.push('')
    }

    for(let i=1; i<=numberOfDaysInCurrentMonth; i++){
        datesOfCurrentMonth.push(i)
    }


    return (
    <>
      <div className="render-cells">
          {blankCells && blankCells.map((blank, index) => {
            return (
                <span key={index} className="render-cell">
                  <span>{blank}</span>
                </span>
            )
          })}
          {datesOfCurrentMonth.map((date, index) => {
            return (
                <CalendarCell
                  key={index}
                  date={date}
                  currentMonth={currentMonth}
                  inspectionsByDay={inspectionsByDay}
                  todayKey={todayKey}
                />
            )
          })}
      </div>
    </>
    )
  };


  return (
    <div className="month-view-layout">
      <div className="calendar">
        {renderHeader()}
        <div className="calendar__body">
          {renderDays()}
          {renderCells()}
        </div>
      </div>
    </div>
  );
};






const CalendarCell = (props) => {
  const {
    precipitationDataForCal, upcomingDataForCal, isLoadingForCal,
    selectedCalProjects,
  } = useContext(DashboardContext)
  const { appDivisionId, getPath } = useContext(AppDivisionContext);
  const [showDayModal, setShowDayModal] = useState(false)
  const [showPrecipitationModal, setShowPrecipitationModal] = useState(false)
  const [showUpcomingModal, setShowUpcomingModal] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [anchorElX, setAnchorElX] = useState(false)
  const [anchorElUpcoming, setAnchorElUpcoming] = useState(false)

  // Mui Custom CSS for inpection detail popover
  const StyledPopover = styled(Popover)(({ theme }) => ({
    [`&.${popoverClasses.root} .render-cells-popover`]: {
      // width: '260px',
      padding: '8px',
      color: '#002230'
    },
    [`&.${popoverClasses.root} li`]: {
      listStyle: 'none',
      padding: '0.25rem 0',
      lineHeight: 1
    },
    [`&.${popoverClasses.root} li:nth-child(1)`]: {
      fontWeight: 900,
      marginBottom: '0.5rem'
    },
    [`&.${popoverClasses.root} li:nth-child(1), li:nth-child(3)`]: {
      whitespace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '25ch',
      '-webkitBoxOrient': 'vertical',
    },
    [`&.${popoverClasses.root} li:nth-child(2) span`]: {
      color: 'rgb(168, 168, 168)'
    },
    [`&.${popoverClasses.root} li p`]: {
      display: 'inline'
    },
    [`&.${popoverClasses.root} .render-cells-popover__cta`]: {
      border: '1px solid #afafaf',
      width: 'fit-content',
      marginTop: '5px',
      padding: '3px',
      borderRadius: '4px',
    },
    [`&.${popoverClasses.root} .render-cells-popover__cta:hover`]: {
      background: 'rgb(228, 228, 228)'
    },
    [`&.${popoverClasses.root} .render-cells-popover__cta a`]: {
      textDecoration: "none"
    },
    [`&.${popoverClasses.root} .render-cells-popover__cta a span`]: {
      borderRadius: '4px',
      padding: '3px',
      color: '#002230',
      fontWeight: 900
    },
  }));

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseX = () => {
    setAnchorElX(null);
  };

  const handleCloseUpcoming = () => {
    setAnchorElUpcoming(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'inspection-popover' : undefined;

  const openX = Boolean(anchorElX);
  const idX = openX ? 'precipitation-popover' : undefined;

  const openUpcoming = Boolean(anchorElUpcoming);
  const idUpcoming = openUpcoming ? 'upcoming-popover' : undefined;

  const { date, currentMonth } = props

  let cellDate = new Date(`${format(startOfMonth(currentMonth), 'M')}/${date}/${format(startOfMonth(currentMonth), 'yyyy')}`)

  // SW2-2343 — the month is bucketed once in the parent, so this is a lookup rather
  // than two filters and a fresh RegExp per cell.
  const dayKey = format(cellDate, 'yyyy-MM-dd')
  const filteredInspection = props.inspectionsByDay?.[dayKey] ?? []

  /**
   * SW2-2343 — the next-inspection shading is gone.
   *
   * It read `displayStatsForCal[0].projectNextInspectionDate`: row 0 of the response,
   * meaningful only while the widget showed a single project, and limited to that case
   * for exactly that reason. It could carry no count, it marked one project's date on a
   * division-wide grid, and the second cell it used to shade advanced by
   * `projectRoutineInterval * 86400000` — a field that aliased `inspection_interval_id`,
   * so the highlight moved by a foreign key rather than a number of days.
   *
   * The teal corner below replaces it and says more: which forms, how many, and for
   * which projects, for every project in the selection rather than the first.
   */

  /**
   * SW2-2338 — the projects over their *own* threshold on this day.
   *
   * The server decides. Each project carries its own rain and snow thresholds, so a
   * single "is this over" cannot be computed here for a selection — and the comparison
   * this replaces was wrong even for one project: `amount >= threshold` with a NULL
   * threshold evaluates as `amount >= 0`, which is true at zero. 965 of 1,369
   * precipitation projects have no snow threshold, so they flagged every day carrying
   * any weather row.
   */
  const precipitationForDay = precipitationDataForCal?.days?.[dayKey] ?? []

  /**
   * SW2-2343 — what is due on this day: a form assigned under Project Settings >
   * Inspection Settings carrying its next date, or a one-time inspection someone
   * scheduled. Both arrive from one query, already keyed by day.
   *
   * **Deliberately not filtered by the Inspection type control**, which is why this is
   * a plain lookup rather than a `.filter`.
   *
   * That control lists rows from `inspection_types` — Routine, Post Storm, Final — and
   * an inspection only acquires one when it is *created*, through
   * `inspection_inspection_types`. An upcoming inspection does not exist yet, so it has
   * no such type: the nearest column, `project_inspection_templates.inspection_type`,
   * is empty on 7,701 of the 7,752 schedulable rows (99.3%).
   *
   * The first cut filtered `selectedCalInspectionTypes.includes(row.formTypeId)`, which
   * was wrong in the quiet way. `formTypeId` is an `inspection_template_type` id — SWP,
   * Dewatering, Industrial — a different table from `inspection_types`, and the two id
   * spaces overlap (1-11 against 1-13). So the comparison never failed loudly; picking
   * "Routine" (id 1) matched every SWP form (also id 1) and looked like it worked.
   *
   * The submitted markers are still type-filtered, server-side, because a submitted
   * inspection does carry a real type. The asymmetry is a product question rather than
   * a bug: whether narrowing to "Post Storm" should dim the upcoming corners, leave
   * them, or say somewhere that they are not narrowed.
   */
  const upcomingForDay = upcomingDataForCal?.days?.[dayKey] ?? []

  /**
   * SW2-2343 — due on a day that has already passed.
   *
   * A whole-cell property, not a per-row one: every row in `upcomingForDay` is filed
   * under this cell's day by definition, so a cell is entirely past due or entirely
   * not. That is what lets one corner serve both states rather than splitting into two.
   *
   * Strictly before today. An inspection due *today* is due, not late, and colouring it
   * red would cry wolf on the one day somebody can still act on it normally. Both sides
   * are `yyyy-MM-dd`, so the lexicographic compare is also the chronological one.
   */
  const isPastDue = Boolean(upcomingForDay.length) && dayKey < props.todayKey
  const upcomingLabel = isPastDue ? 'past due' : 'upcoming'

  /**
   * One inspection gets the popover, several get the modal.
   *
   * The modal exists because the popover could only ever show `filteredInspection[0]`,
   * which under-reported any day holding more than one. For a single inspection it is
   * the wrong shape — a full-height dialog to say four things about one row.
   */
  /**
   * Anything open belongs to the data that was on screen when it was opened.
   *
   * Switching division, changing the filter or paging the month all replace
   * `displayStatsForCal` underneath an open modal or popover, which would otherwise
   * stay up showing the new month's rows for this cell — or "0 inspections" for a day
   * that no longer has any.
   */
  useEffect(() => {
    setShowDayModal(false)
    setShowPrecipitationModal(false)
    setShowUpcomingModal(false)
    setAnchorEl(null)
    setAnchorElX(null)
    setAnchorElUpcoming(null)
    // The cell's own date and the selection are dependencies too. The two data props
    // do change when either of those changes, but only after the render that has
    // already handed the modal its new props — so for one frame it can show the new
    // day's rows under the old day's heading. Naming them closes it before the swap.
    // `inspectionsByDay`, not `filteredInspection`. The latter is
    // `inspectionsByDay?.[dayKey] ?? []`, and on a day with no submitted inspections
    // that `?? []` builds a new array every render — so this effect re-ran on every
    // render and shut whatever had just been opened. It made the upcoming modal and
    // popover impossible to open on exactly the days they matter, the future ones,
    // which have no submitted inspections by definition. The memo in the parent is a
    // stable reference that changes only when the data does.
  }, [props.inspectionsByDay, precipitationDataForCal, upcomingDataForCal, appDivisionId, dayKey, selectedCalProjects])

  const onInspectionIconClick = (event) => {
    if (filteredInspection.length === 1) setAnchorEl(event.currentTarget);
    else setShowDayModal(true);
  }

  // Same rule as the inspections side: one project gets the popover, several get the
  // modal. A full-height dialog to state two numbers about one project is the wrong
  // shape, and it covers the calendar behind it.
  const onPrecipitationIconClick = (event) => {
    if (precipitationForDay.length === 1) setAnchorElX(event.currentTarget);
    else setShowPrecipitationModal(true);
  }

  // SW2-2343 — the same rule again, for the third marker.
  const onUpcomingIconClick = (event) => {
    if (upcomingForDay.length === 1) setAnchorElUpcoming(event.currentTarget);
    else setShowUpcomingModal(true);
  }

  return (
    <>
      <span className="render-cell" id={date}>
        { isLoadingForCal ? <>...</> :
        (<>
        {filteredInspection.length ?
          // A button, not a clickable span: this is the only way into the day's
          // inspections, and a span cannot be focused or activated from the keyboard
          // and carries no accessible name.
          <button
            type="button"
            className="render-cell__submitted"
            onClick={onInspectionIconClick}
            aria-label={`${filteredInspection.length} ${filteredInspection.length === 1 ? 'inspection' : 'inspections'} submitted on ${format(cellDate, 'MMMM d, yyyy')}`}
          >
            <FontAwesomeIcon icon={ faCalendarCheck } className="render-cell__list-icon" />
            {/* The count matters now: with a whole division in view a day routinely holds
                dozens of inspections, and the icon alone said only "at least one".
                A count of 1 is rendered but hidden on desktop, where the icon already
                says it — on a phone the icons are dropped and the number is all there
                is, so it has to be there to be shown. */}
            <span
              className={`render-cell__count${filteredInspection.length === 1 ? ' render-cell__count--single' : ''}`}
              aria-hidden="true"
            >
              {filteredInspection.length}
            </span>
          </button>
          : <></>}
        <span>{date}</span>
        { precipitationForDay.length ?
          <button
            type="button"
            className="render-cell__precipitation"
            onClick={(event) => onPrecipitationIconClick(event)}
            aria-label={`${precipitationForDay.length} ${precipitationForDay.length === 1 ? 'project' : 'projects'} over precipitation threshold on ${format(cellDate, 'MMMM d, yyyy')}`}
          >
            {/* Count first: this button sits at the cell's top-right, so the number
                belongs on its left to read inward rather than off the edge. */}
            <span
              className={`render-cell__count${precipitationForDay.length === 1 ? ' render-cell__count--single' : ''}`}
              aria-hidden="true"
            >
              {precipitationForDay.length}
            </span>
            <FontAwesomeIcon icon={ faCloudShowersHeavy } className="render-cell__cloud-icon" />
          </button>
          : <></> }

        {/* SW2-2343 — upcoming, bottom-left. Icon first, like the submitted marker on
            the same side, so the count reads inward from the edge.

            The check-mark calendar went to the submitted corner on review — a tick
            reads as "done", which is what that corner means — leaving the clipboard
            here for work still to do.

            The colour is the warning orange, set on review. The ticket had specified a
            teal on the reasoning that upcoming is not a problem and that warning should
            be saved for overdue, which is pencilled in for the remaining corner — so
            whoever adds overdue needs a colour that reads as more urgent than this one
            rather than a second orange. */}
        {upcomingForDay.length ?
          <button
            type="button"
            className={`render-cell__upcoming${isPastDue ? ' render-cell__upcoming--past-due' : ''}`}
            onClick={(event) => onUpcomingIconClick(event)}
            aria-label={`${upcomingForDay.length} ${upcomingForDay.length === 1 ? 'inspection' : 'inspections'} ${upcomingLabel} on ${format(cellDate, 'MMMM d, yyyy')}`}
          >
            <FontAwesomeIcon icon={ faClipboardList } className="render-cell__upcoming-icon" />
            <span
              className={`render-cell__count${upcomingForDay.length === 1 ? ' render-cell__count--single' : ''}`}
              aria-hidden="true"
            >
              {upcomingForDay.length}
            </span>
          </button>
          : <></> }
        </>)
        }
        
        <div>
          {filteredInspection.length === 1 &&
            <StyledPopover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
            >
              <div className="render-cells-popover">
                <li>{filteredInspection[0].projectName}</li>
                <li><span>Date Of Inspection:</span> <p>{filteredInspection[0].inspectionDOI}</p></li>
                <li><span>Inspection Type:</span> <p>{filteredInspection[0].inspectionType}</p></li>
                <li><span>Inspector Name:</span> <p>{filteredInspection[0].inspector}</p></li>
                <section className="render-cells-popover__cta">
                  <Link to={`/divisions/${appDivisionId}/projects/${filteredInspection[0].projectId}/inspections`}>
                    <span><FontAwesomeIcon icon={ faEye }/> View Project</span>
                  </Link>
                </section>
              </div>
            </StyledPopover>
          }
        </div>

        <CalendarDayModal
          show={showDayModal}
          onClose={() => setShowDayModal(false)}
          dateLabel={format(cellDate, 'MMMM d, yyyy')}
          inspections={filteredInspection}
          divisionId={appDivisionId}
        />

        <PrecipitationDayModal
          show={showPrecipitationModal}
          onClose={() => setShowPrecipitationModal(false)}
          day={dayKey}
          dateLabel={format(cellDate, 'MMMM d, yyyy')}
          overThreshold={precipitationForDay}
          path={getPath('/calendar/precipitation/day')}
          selectionParams={selectedCalProjects.length ? { projectIds: selectedCalProjects.join(',') } : {}}
        />

        <div>
          { precipitationForDay.length === 1 &&
            <StyledPopover
              id={idX}
              open={openX}
              anchorEl={anchorElX}
              onClose={handleCloseX}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
            >
              <div className="render-cells-popover">
                <li>{precipitationForDay[0].projectName}</li>
                <li><span>Rain:</span> <p>{Number(precipitationForDay[0].rain).toFixed(2)}&Prime;</p></li>
                <li><span>Snow:</span> <p>{Number(precipitationForDay[0].snow).toFixed(2)}&Prime;</p></li>
                {precipitationForDay[0].isManual &&
                  <li><span>Manually recorded reading</span></li>}
              </div>
            </StyledPopover>
          }
        </div>

        <UpcomingDayModal
          show={showUpcomingModal}
          onClose={() => setShowUpcomingModal(false)}
          dateLabel={format(cellDate, 'MMMM d, yyyy')}
          upcoming={upcomingForDay}
          divisionId={appDivisionId}
          isPastDue={isPastDue}
        />

        <div>
          { upcomingForDay.length === 1 &&
            <StyledPopover
              id={idUpcoming}
              open={openUpcoming}
              anchorEl={anchorElUpcoming}
              onClose={handleCloseUpcoming}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
            >
              <div className="render-cells-popover">
                <li>{upcomingForDay[0].projectName}</li>
                <li><span>Form:</span> <p>{upcomingForDay[0].formName || '\u2014'}</p></li>
                <li><span>Inspection Type:</span> <p>{upcomingForDay[0].formType || '\u2014'}</p></li>
                {upcomingForDay[0].frequency &&
                  <li><span>Frequency:</span> <p>{upcomingForDay[0].frequency}</p></li>}
                {/* 'Unassigned' rather than a blank: the name is null both when nobody
                    was assigned and when the assignee's account was deleted, and an
                    empty line would read as a rendering fault instead of a real state. */}
                <li><span>Inspector Name:</span> <p>{upcomingForDay[0].inspectorName || 'Unassigned'}</p></li>
                {isPastDue &&
                  <li><span className="render-cells-popover__past-due">Past due</span></li>}
                {upcomingForDay[0].kind === 'one_time' &&
                  <li><span>One-time inspection</span></li>}
                {upcomingForDay[0].notes &&
                  <li><span>Notes:</span> <p>{upcomingForDay[0].notes}</p></li>}
                <section className="render-cells-popover__cta">
                  <Link to={`/divisions/${appDivisionId}/projects/${upcomingForDay[0].projectId}/inspections`}>
                    <span><FontAwesomeIcon icon={ faEye }/> View Project</span>
                  </Link>
                </section>
              </div>
            </StyledPopover>
          }
        </div>
      </span>
    </>
  )
}




export default MonthViewLayout;

